import React, { useEffect, useState } from 'react';
import { T, useLanguages, useLocale } from '@transifex/react';
import { ChevronDown, Globe01 } from 'src/ui-new/icons';
import { useCompany } from 'src/app/Company.store';
import { useUserMaybe, useUserStore } from 'src/app/User.store';
import { setDatetimeLocale } from 'src/utils';
import {
  getVisibleLanguages,
  isLanguageSupported,
  setTransifexLocale,
} from 'src/components/I18n';
import { handleApiChange } from 'src/components/shared/FormChangeApiUrl';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Shared data wiring for the login + sidebar language pickers. Resolves the
 * tenant's visible languages, the current locale, and exposes a setter that
 * persists for logged-in users and pushes through Transifex.
 */
const useLanguagePicker = () => {
  const { data: company } = useCompany();
  const userMaybe = useUserMaybe();
  const languages = useLanguages();
  const locale = useLocale();
  const [loading, setLoading] = useState(false);

  const language = company?.default_language?.toLowerCase();
  const supported = company?.supported_languages;
  const visibleLanguages = getVisibleLanguages(
    languages,
    supported,
    company?.default_language
  );
  const currentLanguage = languages.find(
    (l: $TSFixMe) => l.code === locale
  ) as $TSFixMe;

  useEffect(() => {
    const userLang = userMaybe?.language?.toLowerCase();
    const savedLocale =
      (isLanguageSupported(userLang, supported, company?.default_language)
        ? userLang
        : undefined) ||
      language ||
      'en';
    setTransifexLocale(savedLocale);
    setDatetimeLocale(savedLocale);
  }, [language, userMaybe?.language, supported, company?.default_language]);

  const setLanguage = async (value: string) => {
    setLoading(true);
    if (userMaybe) {
      await useUserStore.getState().update({ language: value });
    }
    setTransifexLocale(value);
    setLoading(false);
  };

  return {
    locale,
    loading,
    visibleLanguages,
    hasMultipleLanguages: visibleLanguages.length > 1,
    currentLanguage,
    setLanguage,
  };
};

/**
 * Whether the tenant exposes more than one selectable language. Lets callers
 * (LoginShell, sidebar) skip rendering the picker entirely when there's
 * nothing to pick.
 *
 * Predicts the answer synchronously from the company config (supported ∪
 * default, matching `getVisibleLanguages`) instead of waiting for Transifex's
 * async `useLanguages()`. Otherwise the value flips `false` → `true` after
 * first paint, which grows the login footer and visibly shifts the
 * vertically-centered section (the logo "jumps").
 */
export const useHasMultipleLanguages = (): boolean => {
  const { data: company } = useCompany();
  const normalize = (raw: string | undefined) =>
    (raw || '').toLowerCase().split(/[-_]/)[0];
  const codes = new Set(
    (company?.supported_languages ?? []).map(normalize).filter(Boolean)
  );
  const fallback = normalize(company?.default_language);
  if (fallback) codes.add(fallback);
  return codes.size > 1;
};

/**
 * DropdownMenu-based language picker. Used in the post-login portal sidebar.
 * Triple-click on the trigger fires the dev-only API URL switcher.
 */
export const LanguagePicker: React.FC<{ triggerClassName?: string }> = ({
  triggerClassName,
}) => {
  const {
    locale,
    loading,
    visibleLanguages,
    hasMultipleLanguages,
    currentLanguage,
    setLanguage,
  } = useLanguagePicker();

  if (!hasMultipleLanguages) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={loading}
        onClick={handleApiChange}
        aria-label="Switch language"
        className={cn(
          'group relative flex h-12 w-full cursor-pointer items-center rounded-[var(--wh-radius-m,12px)] border border-border bg-background py-0 pl-14 pr-4 text-base font-medium text-muted-foreground shadow-[0_1px_0_var(--border-default)] outline-none transition-colors hover:bg-muted data-[state=open]:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
          triggerClassName
        )}
      >
        <Globe01 className="pointer-events-none absolute left-4 top-1/2 size-6 -translate-y-1/2 text-muted-foreground" />
        <span className="flex-1 text-left">
          {currentLanguage?.localized_name}
        </span>
        <ChevronDown className="size-6 text-muted-foreground transition-transform duration-200 ease-out group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        {visibleLanguages.map(({ code, localized_name }: $TSFixMe) => (
          <DropdownMenuItem
            key={code}
            onSelect={() => setLanguage(code)}
            className={cn(
              code === locale &&
                'bg-accent font-medium text-foreground data-[highlighted]:bg-accent'
            )}
          >
            {localized_name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export type LanguagePickerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const Flag: React.FC<{ code: string; className?: string }> = ({
  code,
  className,
}) => {
  const head = code.toLowerCase().split(/[-_]/)[0];
  const commonProps = {
    width: 20,
    height: 20,
    viewBox: '0 0 20 20',
    fill: 'none' as const,
    'aria-hidden': true,
    className,
  };
  switch (head) {
    case 'da':
      return (
        <svg {...commonProps}>
          <path
            d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20Z"
            fill="#F0F0F0"
          />
          <path
            d="M7.82621 8.69566H19.9154C19.2762 3.78914 15.0808 0 10.0001 0C9.2534 0 8.52617 0.082539 7.82617 0.237695L7.82621 8.69566Z"
            fill="#D80027"
          />
          <path
            d="M5.21672 8.69561V1.21588C2.4709 2.71408 0.505195 5.46217 0.0839844 8.69564L5.21672 8.69561Z"
            fill="#D80027"
          />
          <path
            d="M5.21668 11.3043H0.0839844C0.505195 14.5378 2.4709 17.2859 5.21672 18.784L5.21668 11.3043Z"
            fill="#D80027"
          />
          <path
            d="M7.82617 11.3044V19.7623C8.52617 19.9174 9.2534 20 10.0001 20C15.0808 20 19.2762 16.2108 19.9154 11.3043H7.82617V11.3044Z"
            fill="#D80027"
          />
        </svg>
      );
    case 'no':
      return (
        <svg {...commonProps}>
          <path
            d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20Z"
            fill="#F0F0F0"
          />
          <path
            d="M0.34375 12.6088C0.922344 14.7556 2.19754 16.6163 3.9123 17.9338V12.6088H0.34375Z"
            fill="#D80027"
          />
          <path
            d="M9.13086 19.962C9.4175 19.9867 9.70738 20.0001 10.0004 20.0001C14.6207 20.0001 18.5084 16.8664 19.6559 12.6088H9.13086V19.962Z"
            fill="#D80027"
          />
          <path
            d="M19.6559 7.39129C18.5084 3.13363 14.6207 0 10.0004 0C9.70738 0 9.4175 0.0133594 9.13086 0.0380469V7.39129H19.6559Z"
            fill="#D80027"
          />
          <path
            d="M3.9123 2.06616C2.19754 3.38374 0.922344 5.2444 0.34375 7.39124H3.9123V2.06616Z"
            fill="#D80027"
          />
          <path
            d="M19.9154 8.69563H7.82613L7.82609 0.237671C6.90195 0.442593 6.02582 0.774741 5.21738 1.21587V8.69556V8.6956H0.0846484C0.0289844 9.12259 0 9.5579 0 9.99997C0 10.442 0.0289844 10.8774 0.0846484 11.3043H5.21734H5.21738V18.784C6.02582 19.2251 6.90195 19.5574 7.82609 19.7622V11.3044V11.3043H19.9154C19.9709 10.8774 20 10.442 20 9.99997C20 9.5579 19.9709 9.12259 19.9154 8.69563Z"
            fill="#0052B4"
          />
        </svg>
      );
    case 'en':
      return (
        <svg {...commonProps}>
          <path
            d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20Z"
            fill="#F0F0F0"
          />
          <path
            d="M9.56445 9.99995H19.9992C19.9992 9.09737 19.8789 8.22299 19.6547 7.39124H9.56445V9.99995Z"
            fill="#D80027"
          />
          <path
            d="M9.56445 4.78266H18.5315C17.9193 3.78375 17.1366 2.90083 16.2241 2.17395H9.56445V4.78266Z"
            fill="#D80027"
          />
          <path
            d="M10.0002 20C12.3537 20 14.5169 19.1865 16.2251 17.826H3.77539C5.48359 19.1865 7.64676 20 10.0002 20Z"
            fill="#D80027"
          />
          <path
            d="M1.46699 15.2174H18.5315C19.023 14.4154 19.4041 13.5389 19.6548 12.6086H0.34375C0.594414 13.5389 0.975547 14.4154 1.46699 15.2174Z"
            fill="#D80027"
          />
          <path
            d="M4.63219 1.56164H5.54348L4.69582 2.17746L5.01961 3.17391L4.17199 2.55809L3.32437 3.17391L3.60406 2.31309C2.85773 2.93477 2.20359 3.66313 1.66453 4.47469H1.95652L1.41695 4.86668C1.33289 5.00691 1.25227 5.14937 1.175 5.29395L1.43266 6.08695L0.951953 5.7377C0.832461 5.99086 0.723164 6.24973 0.624922 6.51398L0.908789 7.38773H1.95652L1.10887 8.00356L1.43266 9L0.585039 8.38418L0.0773047 8.75309C0.0264844 9.1616 0 9.57769 0 10H10C10 4.47719 10 3.82609 10 0C8.02453 0 6.18301 0.573047 4.63219 1.56164ZM5.01961 9L4.17199 8.38418L3.32437 9L3.64816 8.00356L2.80051 7.38773H3.84824L4.17199 6.39129L4.49574 7.38773H5.54348L4.69582 8.00356L5.01961 9ZM4.69582 5.09051L5.01961 6.08695L4.17199 5.47113L3.32437 6.08695L3.64816 5.09051L2.80051 4.47469H3.84824L4.17199 3.47824L4.49574 4.47469H5.54348L4.69582 5.09051ZM8.60656 9L7.75895 8.38418L6.91133 9L7.23512 8.00356L6.38746 7.38773H7.4352L7.75895 6.39129L8.0827 7.38773H9.13043L8.28277 8.00356L8.60656 9ZM8.28277 5.09051L8.60656 6.08695L7.75895 5.47113L6.91133 6.08695L7.23512 5.09051L6.38746 4.47469H7.4352L7.75895 3.47824L8.0827 4.47469H9.13043L8.28277 5.09051ZM8.28277 2.17746L8.60656 3.17391L7.75895 2.55809L6.91133 3.17391L7.23512 2.17746L6.38746 1.56164H7.4352L7.75895 0.565195L8.0827 1.56164H9.13043L8.28277 2.17746Z"
            fill="#0052B4"
          />
        </svg>
      );
    case 'fi':
      return (
        <svg {...commonProps}>
          <path
            d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20Z"
            fill="#F0F0F0"
          />
          <path
            d="M19.9154 8.69564H7.82613L7.82609 0.237671C6.90195 0.442593 6.02582 0.774741 5.21738 1.21587V8.69556V8.6956H0.0846484C0.0289844 9.12259 0 9.55791 0 9.99998C0 10.442 0.0289844 10.8774 0.0846484 11.3043H5.21734H5.21738V18.784C6.02582 19.2251 6.90195 19.5574 7.82609 19.7622V11.3044V11.3044H19.9154C19.9709 10.8774 20 10.442 20 9.99998C20 9.55791 19.9709 9.12259 19.9154 8.69564Z"
            fill="#0052B4"
          />
        </svg>
      );
    case 'sv':
      return (
        <svg {...commonProps}>
          <path
            d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20Z"
            fill="#FFDA44"
          />
          <path
            d="M7.82621 8.69566H19.9154C19.2762 3.78914 15.0808 0 10.0001 0C9.25344 0 8.52613 0.0825391 7.82617 0.237695L7.82621 8.69566Z"
            fill="#0052B4"
          />
          <path
            d="M5.21672 8.69567V1.21594C2.47086 2.71415 0.505195 5.46223 0.0839844 8.69571L5.21672 8.69567Z"
            fill="#0052B4"
          />
          <path
            d="M5.21668 11.3043H0.0839844C0.505195 14.5378 2.47086 17.2859 5.21672 18.784L5.21668 11.3043Z"
            fill="#0052B4"
          />
          <path
            d="M7.82617 11.3044V19.7623C8.52613 19.9174 9.25344 20 10.0001 20C15.0808 20 19.2762 16.2108 19.9154 11.3043H7.82617V11.3044Z"
            fill="#0052B4"
          />
        </svg>
      );
    default:
      return null;
  }
};

/**
 * Dialog-based language picker. Used on the login surface — the trigger lives
 * in the LoginShell footer ("Change language") and opens this modal.
 */
export const LanguagePickerModal: React.FC<LanguagePickerModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { locale, loading, visibleLanguages, setLanguage } =
    useLanguagePicker();

  const onSelect = async (value: string) => {
    await setLanguage(value);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-1 rounded-xl p-1 shadow-none duration-300 ease-out sm:max-w-[480px]"
      >
        <div className="flex w-full items-center justify-between">
          <DialogTitle className="px-4 py-2 text-base font-semibold leading-[1.5]">
            <T _str="Change language" />
          </DialogTitle>
          <DialogDescription className="sr-only">
            <T _str="Pick the language you want to use." />
          </DialogDescription>
          <DialogClose className="flex h-12 cursor-pointer items-center rounded-lg px-4 py-2 text-xs font-medium uppercase leading-none tracking-[0.05em] text-muted-foreground outline-hidden transition-colors hover:bg-muted focus-visible:bg-muted">
            <T _str="Cancel" />
          </DialogClose>
        </div>
        <div className="flex flex-col gap-1" role="radiogroup">
          {visibleLanguages.map(({ code, localized_name }: $TSFixMe) => {
            const isCurrent = code === locale;
            return (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={isCurrent}
                disabled={loading}
                onClick={() => onSelect(code)}
                className={cn(
                  'flex h-12 w-full cursor-pointer items-center gap-4 rounded-lg px-4 py-2 text-base font-medium leading-[1.5] outline-hidden transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  isCurrent
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:bg-muted focus-visible:bg-muted'
                )}
              >
                <Flag code={code} className="size-5 shrink-0" />
                <span>{localized_name}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
